import { OCRProvider } from '../interfaces/OCRProvider.js';
import { supabase } from '../../../lib/supabase';

/**
 * OCR.Space Provider — Seguro
 *
 * Llama a la Edge Function 'ocr-financiero' que actúa como proxy.
 * La API Key de OCR.Space NUNCA se expone al frontend.
 * Se guarda en Supabase Secrets: OCR_SPACE_API_KEY
 */
export class OCRSpaceProvider extends OCRProvider {
  static PROVIDER_NAME = 'OCR.Space';

  getProviderName() {
    return OCRSpaceProvider?.PROVIDER_NAME;
  }

  /**
   * Extract raw text from a file via Supabase Edge Function.
   * The Edge Function calls OCR.Space with the secret API key.
   */
  async extractText(file, mimeType) {
    // Obtener URL de la Edge Function desde Supabase
    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('VITE_SUPABASE_URL no configurado');
    }

    const edgeFunctionUrl = `${supabaseUrl}/functions/v1/ocr-financiero`;

    // Obtener token de autenticación
    const { data: { session } } = await supabase?.auth?.getSession();
    const authToken = session?.access_token;

    const formData = new FormData();
    formData?.append('file', file, file?.name || 'factura');
    formData?.append('language', this._mapLanguage(this.config?.language));

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller?.abort(), (this.config?.timeout || 60000));

    let response;
    try {
      response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          // Incluir token de auth si está disponible
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          // NO incluir apikey aquí — la Edge Function la obtiene de Secrets
        },
        body: formData,
        signal: controller?.signal,
      });
    } catch (err) {
      clearTimeout(timeoutId);
      if (err?.name === 'AbortError') {
        throw new Error(`OCR: Tiempo de espera agotado. Inténtalo de nuevo.`);
      }
      throw new Error(`OCR: Error de red — ${err?.message}`);
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response?.ok) {
      let errDetails = '';
      try { const errJson = await response?.json(); errDetails = errJson?.error || errJson?.details || ''; } catch (_) {}
      throw new Error(`OCR: Error ${response.status} — ${errDetails || response.statusText}`);
    }

    let data;
    try {
      data = await response?.json();
    } catch {
      throw new Error('OCR: La respuesta no es JSON válido');
    }

    if (!data?.success || data?.IsErroredOnProcessing) {
      throw new Error(`OCR: ${data?.error || data?.details || 'Error desconocido'}`);
    }

    if (!data?.ParsedResults?.length) {
      throw new Error('OCR: No se pudo extraer texto del documento. Verifica que el archivo sea legible.');
    }

    const allPages = data?.ParsedResults || [];
    const textoCompleto = allPages?.map(p => p?.ParsedText || '')?.join('\n\n--- PÁGINA ---\n\n')?.trim();

    if (!textoCompleto) {
      throw new Error('OCR: El documento no contiene texto legible. Prueba con una imagen de mayor calidad.');
    }

    const parsed = this._parseRawText(textoCompleto);

    return {
      proveedor_texto: parsed?.proveedor_texto,
      cif_texto: parsed?.cif_texto,
      fecha_texto: parsed?.fecha_texto,
      numero_factura: parsed?.numero_factura,
      lineas_texto: parsed?.lineas_texto,
      total_texto: parsed?.total_texto,
      texto_completo: textoCompleto,
      provider: OCRSpaceProvider?.PROVIDER_NAME,
      confidence: this._estimateConfidence(textoCompleto, allPages),
      warnings: this._extractWarnings(data, textoCompleto),
    };
  }

  _parseRawText(text) {
    const lines = text?.split('\n')?.map(l => l?.trim())?.filter(Boolean);

    const cifMatch = text?.match(/\b([A-Z]\d{7}[A-Z0-9]|\d{8}[A-Z])\b/i);
    const cif_texto = cifMatch?.[1] || null;

    const facturaPatterns = [
      /(?:factura|fra|invoice|nº|num|número)[:\s#]*([A-Z0-9\-\/]+)/i,
      /\b([A-Z]{1,4}[-\/]?\d{4,10})\b/,
    ];
    let numero_factura = null;
    for (const pat of facturaPatterns) {
      const m = text?.match(pat);
      if (m?.[1] && m?.[1]?.length >= 3) { numero_factura = m?.[1]?.trim(); break; }
    }

    const datePatterns = [
      /\b(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4})\b/,
      /\b(\d{4}[\/\-\.]\d{2}[\/\-\.]\d{2})\b/,
    ];
    let fecha_texto = null;
    for (const pat of datePatterns) {
      const m = text?.match(pat);
      if (m?.[1]) { fecha_texto = m?.[1]; break; }
    }

    const totalPatterns = [
      /(?:total|importe total|total factura)[:\s]*([0-9.,]+)\s*€?/i,
      /(?:total a pagar|total iva incluido)[:\s]*([0-9.,]+)/i,
    ];
    let total_texto = null;
    for (const pat of totalPatterns) {
      const m = text?.match(pat);
      if (m?.[1]) { total_texto = m?.[1]?.trim(); break; }
    }

    const proveedor_texto = lines?.slice(0, 5)?.find(l =>
      l?.length > 3 &&
      !l?.match(/^(factura|invoice|fecha|date|nif|cif|iva)/i) &&
      !l?.match(/^\d/)
    ) || null;

    const lineas_texto = lines?.filter(l =>
      l?.match(/\d+[.,]\d+/) &&
      l?.length > 5 &&
      !l?.match(/^(total|subtotal|base|iva|descuento)/i)
    );

    return { proveedor_texto, cif_texto, fecha_texto, numero_factura, lineas_texto, total_texto };
  }

  _estimateConfidence(text, pages) {
    if (!text || text?.length < 50) return 20;
    const hasNumbers = /\d+[.,]\d+/?.test(text);
    const hasDate = /\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}/?.test(text);
    const hasCif = /[A-Z]\d{7}[A-Z0-9]/i?.test(text);
    const avgPageConfidence = pages?.reduce((s, p) => s + (p?.TextOverlay?.MeanConfidence || 0), 0) / (pages?.length || 1);
    let score = 50;
    if (hasNumbers) score += 15;
    if (hasDate) score += 10;
    if (hasCif) score += 10;
    if (avgPageConfidence > 80) score += 15;
    else if (avgPageConfidence > 60) score += 8;
    return Math.min(score, 95);
  }

  _extractWarnings(data, text) {
    const warnings = [];
    if (text?.length < 100) warnings?.push('Texto extraído muy corto — posible baja calidad de imagen');
    if (data?.ParsedResults?.some(p => p?.FileParseExitCode === 2)) {
      warnings?.push('Algunas páginas no pudieron procesarse completamente');
    }
    return warnings;
  }

  _mapLanguage(lang) {
    const map = { spa: 'spa', es: 'spa', eng: 'eng', en: 'eng', fra: 'fre', por: 'por' };
    return map?.[lang] || 'spa';
  }
}

export default OCRSpaceProvider;
